/**
 * Non-QM helpers.
 *
 * "Non-QM" is a UI-only Mortgage Type. On the wire, Vesta still stores
 * `loanProduct.mortgageType = "Conventional"` plus `loan.nonQualifiedMortgageIndicator = true`.
 * The Loan tab dropdown surfaces "Non-QM" as a fifth option; everything else is just
 * derivation between the dropdown selection and the four Vesta fields:
 *
 *   - loan.nonQualifiedMortgageIndicator
 *   - loanProduct.alternativeIncomeVerificationType
 *   - loanProduct.alternativeIncomeDocumentationNumberOfMonths
 *   - loan.underwrittenUsingDSCRIndicator
 *   - loan.subjectPropertyDSCREstimate (DSCR only)
 */

export const NON_QM_MORTGAGE_TYPE_VALUE = 'Non-QM';
export const VESTA_BASE_MORTGAGE_TYPE_FOR_NON_QM = 'Conventional';

/** Vesta AlternativeIncomeVerificationType enum subset surfaced on the UI. */
export const DOC_TYPE_FULL = 'FullDocumentation';
export const DOC_TYPE_PERSONAL_BANK = 'PersonalBankStatement';
export const DOC_TYPE_BUSINESS_BANK = 'BusinessBankStatement';
export const DOC_TYPE_DSCR = 'DSCR';
export const DOC_TYPE_PROFIT_LOSS = 'ProfitAndLoss';

/** Disabled "coming soon" placeholders. AssetDepletion is a real Vesta enum; NoRatio is UI-only. */
export const DOC_TYPE_ASSET_DEPLETION = 'AssetDepletion';
export const DOC_TYPE_NO_RATIO = 'No Ratio';

export const DOC_TYPE_OPTIONS = [
  { value: DOC_TYPE_FULL, label: 'Full Doc' },
  { value: DOC_TYPE_PERSONAL_BANK, label: 'Personal Bank Statements' },
  { value: DOC_TYPE_BUSINESS_BANK, label: 'Business Bank Statements' },
  { value: DOC_TYPE_DSCR, label: 'DSCR' },
  { value: DOC_TYPE_PROFIT_LOSS, label: 'P&L' },
  { value: DOC_TYPE_ASSET_DEPLETION, label: 'Asset Depletion (coming soon)', disabled: true },
  { value: DOC_TYPE_NO_RATIO, label: 'No Ratio (coming soon)', disabled: true },
];

export const BANK_STATEMENT_DOC_TYPES = new Set([DOC_TYPE_PERSONAL_BANK, DOC_TYPE_BUSINESS_BANK]);

/** Vesta AlternativeIncomeDocumentationNumberOfMonths enum (only the two values the UI surfaces). */
export const BANK_STATEMENT_MONTHS_OPTIONS = [
  { value: '_12', label: '12' },
  { value: '_24', label: '24' },
];

export const isNonQmIndicatorTrue = (v) => v === true || v === 'True' || v === 'true';

/**
 * Normalize Non-QM fields from GET /loans into camelCase form keys (handles PascalCase
 * payloads and string booleans from legacy responses).
 */
export const normalizeNonQmFormSnapshot = (nonQm) => {
  if (!nonQm || typeof nonQm !== 'object') return {};
  const n = { ...nonQm };
  const pick = (camel, pascal) => {
    if (n[camel] === undefined && n[pascal] !== undefined) n[camel] = n[pascal];
    if (pascal in n) delete n[pascal];
  };
  pick('nonQualifiedMortgageIndicator', 'NonQualifiedMortgageIndicator');
  pick('alternativeIncomeVerificationType', 'AlternativeIncomeVerificationType');
  pick('alternativeIncomeDocumentationNumberOfMonths', 'AlternativeIncomeDocumentationNumberOfMonths');
  pick('secondaryAlternativeIncomeVerificationType', 'SecondaryAlternativeIncomeVerificationType');
  pick('dscrCalculationMethod', 'DscrCalculationMethod');
  pick('subjectPropertyDSCR', 'SubjectPropertyDSCR');
  pick('subjectPropertyDSCREstimate', 'SubjectPropertyDSCREstimate');
  pick('underwrittenUsingDSCRIndicator', 'UnderwrittenUsingDSCRIndicator');
  if (n.underwrittenUsingDSCRIndicator === 'True') n.underwrittenUsingDSCRIndicator = true;
  if (n.underwrittenUsingDSCRIndicator === 'False') n.underwrittenUsingDSCRIndicator = false;
  if (typeof n.subjectPropertyDSCREstimate === 'string' && n.subjectPropertyDSCREstimate.trim() !== '') {
    const parsed = Number(n.subjectPropertyDSCREstimate);
    n.subjectPropertyDSCREstimate = Number.isFinite(parsed) ? parsed : null;
  }
  return n;
};

/**
 * Returns "Non-QM" when the loan's nonQualifiedMortgageIndicator is true; otherwise
 * the underlying mortgage type as stored in Vesta. Used to render the dropdown's
 * displayed value and to compare against the form field for the stale-product alert.
 */
export const getEffectiveMortgageType = (loan) => {
  if (!loan) return null;
  if (isNonQmIndicatorTrue(loan.nonQualifiedMortgageIndicator)) {
    return NON_QM_MORTGAGE_TYPE_VALUE;
  }
  return loan.mortgageType ?? null;
};

/**
 * Clear the four Non-QM derived fields on the form. Called when the user switches
 * the Mortgage Type away from "Non-QM" so we don't leave stale Doc Type / DSCR data
 * that would confuse the patch diff or subsequent edits.
 */
export const resetNonQmFieldsForFormReset = (setValue) => {
  setValue('nonQm.alternativeIncomeVerificationType', null, { shouldDirty: true });
  setValue('nonQm.alternativeIncomeDocumentationNumberOfMonths', null, { shouldDirty: true });
  setValue('nonQm.underwrittenUsingDSCRIndicator', null, { shouldDirty: true });
  setValue('nonQm.subjectPropertyDSCREstimate', null, { shouldDirty: true });
};

// ─────────────────────────── Pricing-engine mapping ───────────────────────────
// Mirrors pro-api/SK.Pro.Domain.Logic/Loans/LoanPricingNonQmMapper.cs so a fresh
// in-flight Non-QM edit on the Loan / Non-QM tab can be re-overlayed onto the
// cached pricingRequest before the engine is called.

/** Engine `loan.docType` codes for Non-QM products. */
export const ENGINE_DOC_TYPE = {
  FullDoc: 'FullDoc',
  BankStatements: 'BankStatements',
  DSCR: 'DSCR',
  AssetDepletion: 'AssetDepletion',
  ProfitAndLoss: 'ProfitAndLoss',
};

/**
 * Vesta `alternativeIncomeVerificationType` → engine `loan.docType`.
 * Returns null when no Non-QM doc type applies (caller falls back to default).
 */
export const mapNonQmDocTypeToEngine = (alternativeIncomeVerificationType) => {
  switch (alternativeIncomeVerificationType) {
    case DOC_TYPE_FULL: return ENGINE_DOC_TYPE.FullDoc;
    case DOC_TYPE_PERSONAL_BANK:
    case DOC_TYPE_BUSINESS_BANK: return ENGINE_DOC_TYPE.BankStatements;
    case DOC_TYPE_DSCR: return ENGINE_DOC_TYPE.DSCR;
    case DOC_TYPE_ASSET_DEPLETION: return ENGINE_DOC_TYPE.AssetDepletion;
    case DOC_TYPE_PROFIT_LOSS: return ENGINE_DOC_TYPE.ProfitAndLoss;
    default: return null;
  }
};

/**
 * Vesta (alternativeIncomeVerificationType, alternativeIncomeDocumentationNumberOfMonths)
 * → engine `borrower.bankStatement` code (e.g. "PersonalTwelveMonths").
 * Returns null for non-bank-statement doc types or unsupported month counts.
 */
export const mapNonQmBankStatementToEngine = (alternativeIncomeVerificationType, numberOfMonths) => {
  let prefix = null;
  if (alternativeIncomeVerificationType === DOC_TYPE_PERSONAL_BANK) prefix = 'Personal';
  else if (alternativeIncomeVerificationType === DOC_TYPE_BUSINESS_BANK) prefix = 'Business';
  if (!prefix) return null;

  let suffix = null;
  if (numberOfMonths === '_12' || numberOfMonths === '12') suffix = 'TwelveMonths';
  else if (numberOfMonths === '_24' || numberOfMonths === '24') suffix = 'TwentyFourMonths';
  if (!suffix) return null;

  return prefix + suffix;
};

/** True when the Vesta doc type indicates DSCR underwriting. */
export const isDscrDocType = (alternativeIncomeVerificationType) =>
  alternativeIncomeVerificationType === DOC_TYPE_DSCR;
